import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { api, warmUp } from "../lib/api";
import { useAuth, formatApiError } from "../lib/auth";
import { Coins, SignIn, Eye, EyeSlash, X, TelegramLogo } from "@phosphor-icons/react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [recoverOpen, setRecoverOpen] = useState(false);
  const [ownerTelegram, setOwnerTelegram] = useState("");
  const [retrying, setRetrying] = useState(0);

  // Wake up a sleeping production origin as soon as the login page opens,
  // so the server is ready by the time the user submits credentials.
  useEffect(() => { warmUp(); }, []);

  // Public, no-auth fetch of owner Telegram contact for the password
  // recovery modal. Always fetch — the user is, by definition, anonymous
  // at this point.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/public/owner-contact");
        if (!cancelled) setOwnerTelegram(data?.owner_telegram || "");
      } catch (e) { console.warn("Login: failed to fetch owner contact", e); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (user) return <Navigate to={location.state?.from || "/"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    setRetrying(0);
    try {
      await login(email.trim().toLowerCase(), password, (n) => setRetrying(n));
      navigate(location.state?.from || "/", { replace: true });
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setLoading(false);
      setRetrying(0);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5 py-10">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-3 mb-10 justify-center">
          <span className="inline-flex h-12 w-12 items-center justify-center bg-[#9C5B42] text-white">
            <Coins weight="duotone" size={26} />
          </span>
          <div className="leading-tight">
            <div className="font-serif text-3xl text-[#1E2520]">Reperti</div>
            <div className="label-eyebrow">Accesso privato</div>
          </div>
        </div>

        <div className="card p-7 md:p-8">
          <h1 className="font-serif text-3xl text-[#1E2520] mb-2">Bentornato</h1>
          <p className="text-sm text-[#5C635A] mb-6">
            Inserisci le tue credenziali per accedere al catalogo.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <input
                type="email"
                className="input-base"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  className="input-base pr-12"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C635A] hover:text-[#1E2520]"
                  aria-label={showPwd ? "Nascondi" : "Mostra"}
                >
                  {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {retrying > 0 && (
              <div className="border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 rounded-sm text-sm" data-testid="login-retrying">
                Il server si sta riavviando dopo un periodo di inattività.
                Riprovo automaticamente (tentativo {retrying}/4)… non chiudere la pagina.
              </div>
            )}

            {err && !retrying && (
              <div className="border border-red-300 bg-red-50 text-red-800 px-4 py-3 rounded-sm text-sm" data-testid="login-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary inline-flex items-center justify-center gap-2 w-full"
              data-testid="login-submit"
            >
              <SignIn weight="duotone" size={18} />
              {loading ? "Accesso in corso..." : "Accedi"}
            </button>
          </form>

          <button
            type="button"
            onClick={() => setRecoverOpen(true)}
            className="text-sm text-[#9C5B42] underline mt-5 inline-block"
            data-testid="forgot-password-link"
          >
            Ho perso la password
          </button>
        </div>

        <p className="text-sm text-[#5C635A] text-center mt-6">
          Non hai ancora un account?{" "}
          <Link
            to="/register"
            className="text-[#9C5B42] underline font-medium"
            data-testid="login-go-register"
          >
            Registrati gratis
          </Link>
        </p>
      </div>

      {/* Password recovery modal */}
      {recoverOpen && (
        <div
          className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center px-4"
          onClick={() => setRecoverOpen(false)}
          data-testid="recover-modal"
        >
          <div
            className="card max-w-md w-full p-6 md:p-7 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setRecoverOpen(false)}
              className="absolute top-3 right-3 text-[#5C635A] hover:text-[#1E2520]"
              aria-label="Chiudi"
              data-testid="recover-modal-close"
            >
              <X size={20} />
            </button>
            <h2 className="font-serif text-2xl text-[#1E2520] mb-3">Hai perso la password?</h2>
            <p className="text-sm text-[#5C635A] mb-4 leading-relaxed">
              Per motivi di sicurezza la tua password è memorizzata cifrata e
              <strong> nessuno (neanche il proprietario) può recuperarla</strong>.
              Però il proprietario può generare per te una password temporanea
              che potrai cambiare al primo accesso.
            </p>
            {ownerTelegram ? (
              <div className="rounded-md border border-[#E5E0D8] bg-[#F9F8F6] p-4 mb-4">
                <div className="label-eyebrow mb-2">Contatta il proprietario su Telegram</div>
                <a
                  href={`https://t.me/${ownerTelegram.replace(/^@/, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-[#9C5B42] underline text-lg font-semibold"
                  data-testid="recover-telegram-link"
                >
                  <TelegramLogo weight="fill" size={22} />
                  {ownerTelegram.startsWith("@") ? ownerTelegram : `@${ownerTelegram}`}
                </a>
                <p className="text-xs text-[#5C635A] mt-2">
                  Manda un messaggio indicando la tua email di registrazione.
                  Riceverai una password temporanea.
                </p>
              </div>
            ) : (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-4 mb-4 text-sm text-amber-900">
                Il proprietario non ha ancora configurato un contatto Telegram
                per il recupero password. Riprova più tardi o crea un nuovo
                account.
              </div>
            )}
            <button
              onClick={() => setRecoverOpen(false)}
              className="btn-ghost w-full"
              data-testid="recover-modal-ok"
            >
              Ho capito, chiudi
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
